import React from "react";
import styled, { keyframes, css } from "styled-components";

/* ------------------------------------------------------------------ */
/*  Drift keyframes - transform only (GPU friendly, no layout/paint)  */
/* ------------------------------------------------------------------ */
const driftA = keyframes`
  0%   { transform: translate3d(-6%, -4%, 0) scale(1); }
  50%  { transform: translate3d(8%, 6%, 0) scale(1.12); }
  100% { transform: translate3d(-4%, 10%, 0) scale(0.96); }
`;

const driftB = keyframes`
  0%   { transform: translate3d(10%, 0, 0) scale(1.05); }
  50%  { transform: translate3d(-8%, 8%, 0) scale(0.94); }
  100% { transform: translate3d(6%, -6%, 0) scale(1.1); }
`;

const driftC = keyframes`
  0%   { transform: translate3d(0, 8%, 0) scale(0.95); }
  50%  { transform: translate3d(10%, -6%, 0) scale(1.1); }
  100% { transform: translate3d(-8%, 4%, 0) scale(1); }
`;

const driftD = keyframes`
  0%   { transform: translate3d(-4%, 6%, 0) scale(1); }
  50%  { transform: translate3d(-10%, -8%, 0) scale(1.15); }
  100% { transform: translate3d(6%, 4%, 0) scale(1.02); }
`;

/* ------------------------------------------------------------------ */
/*  Where the four blobs sit for each section variant                  */
/*  (only the offsets we need are set; the rest stay `auto`)           */
/* ------------------------------------------------------------------ */
const positions = {
  /* Hero - a glow tucked into each corner */
  default: {
    main: css`top: -35%; left: -30%;`,
    ember: css`bottom: -35%; right: -30%;`,
    rust: css`bottom: -40%; left: -25%;`,
    amber: css`top: -25%; right: -18%;`,
  },
  /* Projects - long section: top accent, a soft center, and lower right */
  projects: {
    main: css`top: -20%; left: -22%;`,
    ember: css`top: 68%; right: -24%;`,
    rust: css`top: 40%; left: 30%;`,
    amber: css`top: 14%; right: -18%;`,
  },
  /* Experience - 2 glows by the work experience (right), 2 by education (left) */
  experience: {
    main: css`top: 12%; right: -30%;`,
    ember: css`top: 28%; right: -22%;`,
    rust: css`top: 62%; left: -28%;`,
    amber: css`top: 76%; left: -22%;`,
  },
  /* Footer - everything drifts up from the bottom edge */
  footer: {
    main: css`top: 52%; left: -26%;`,
    ember: css`top: 46%; right: -24%;`,
    rust: css`top: 72%; left: 34%;`,
    amber: css`top: 60%; right: 18%;`,
  },
};

const at = (variant, name) =>
  (positions[variant] || positions.default)[name];

/* ------------------------------------------------------------------ */
/*  Base blob - softness comes from the radial-gradient, not blur      */
/* ------------------------------------------------------------------ */
const Blob = styled.span`
  position: absolute;
  display: block;
  border-radius: 50%;
  z-index: 1;
  pointer-events: none;
  mix-blend-mode: screen;
  transform: translate3d(0, 0, 0);
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
  }
`;

const BlobMain = styled(Blob)`
  width: 60vmax;
  height: 60vmax;
  ${({ $variant = "default" }) => at($variant, "main")}
  background: radial-gradient(
    circle at center,
    rgba(255, 90, 54, 0.9) 0%,
    rgba(255, 90, 54, 0.35) 30%,
    rgba(255, 90, 54, 0) 68%
  );
  animation: ${driftA} 22s ease-in-out infinite alternate;
`;

const BlobEmber = styled(Blob)`
  width: 50vmax;
  height: 50vmax;
  ${({ $variant = "default" }) => at($variant, "ember")}
  background: radial-gradient(
    circle at center,
    rgba(193, 61, 20, 0.85) 0%,
    rgba(140, 40, 15, 0.3) 35%,
    rgba(140, 40, 15, 0) 70%
  );
  animation: ${driftB} 26s ease-in-out infinite alternate;
`;

const BlobRust = styled(Blob)`
  width: 55vmax;
  height: 55vmax;
  ${({ $variant = "default" }) => at($variant, "rust")}
  background: radial-gradient(
    circle at center,
    rgba(92, 31, 20, 0.9) 0%,
    rgba(70, 24, 16, 0.35) 40%,
    rgba(70, 24, 16, 0) 72%
  );
  animation: ${driftC} 19s ease-in-out infinite alternate;

  /* the education (left) glow reads a touch brighter */
  ${({ $variant }) =>
    $variant === "experience" &&
    css`
      filter: brightness(1.35) saturate(1.08);
    `}
`;

const BlobAmber = styled(Blob)`
  width: 34vmax;
  height: 34vmax;
  ${({ $variant = "default" }) => at($variant, "amber")}
  background: radial-gradient(
    circle at center,
    rgba(246, 180, 107, 0.55) 0%,
    rgba(246, 180, 107, 0.18) 40%,
    rgba(246, 180, 107, 0) 72%
  );
  animation: ${driftD} 24s ease-in-out infinite alternate;

  /* the education (left) glow reads a touch brighter */
  ${({ $variant }) =>
    $variant === "experience" &&
    css`
      filter: brightness(1.35) saturate(1.08);
    `}
`;

/* ------------------------------------------------------------------ */
/*  Container - dark base + vignette overlay so text stays readable    */
/* ------------------------------------------------------------------ */
const Aurora = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  overflow: hidden;
  pointer-events: none;
  isolation: isolate;
  background: #060303;

  /* Cheaper on phones: keep only the two main glows. */
  @media (max-width: 640px) {
    & > span:nth-child(3),
    & > span:nth-child(4) {
      display: none;
    }
  }

  /* vignette painted above the blobs */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    background: radial-gradient(
      ellipse at center,
      rgba(0, 0, 0, 0) 35%,
      rgba(0, 0, 0, 0.5) 75%,
      rgba(0, 0, 0, 0.85) 100%
    );
  }
`;

const AuroraBackground = ({ variant = "default", className, style }) => (
  <Aurora className={className} style={style} aria-hidden="true">
    <BlobMain $variant={variant} />
    <BlobEmber $variant={variant} />
    <BlobRust $variant={variant} />
    <BlobAmber $variant={variant} />
  </Aurora>
);

export default AuroraBackground;

/* ------------------------------------------------------------------ */
/*  Usage example                                                      */
/*                                                                     */
/*  import styled from "styled-components";                            */
/*  import AuroraBackground from "../components/AuroraBackground";     */
/*                                                                     */
/*  const Section = styled.section`                                    */
/*    position: relative;                                              */
/*    isolation: isolate;   // REQUIRED: keeps the glow behind content */
/*    min-height: 100vh;                                               */
/*    background: #050202;                                             */
/*  `;                                                                 */
/*                                                                     */
/*  const Content = styled.div`                                        */
/*    position: relative;                                              */
/*  `;                                                                 */
/*                                                                     */
/*  const Hero = () => (                                               */
/*    <Section>                                                        */
/*      <AuroraBackground variant="default" />   // or "projects",     */
/*      <Content>                                 // "experience",     */
/*        <h1>Your headline</h1>                  // "footer"          */
/*      </Content>                                                     */
/*    </Section>                                                       */
/*  );                                                                 */
/* ------------------------------------------------------------------ */
